/**
 * @file src/core/Engine.js
 * Vòng đời render + camera + controls.
 *
 * NGUỒN GỐC: nâng từ webGL/three-lessons/src/core/Engine.js. Giữ nguyên những
 * phần đã chín ở đó (smooth zoom lerp, wheel normalize theo deltaMode,
 * distance-scaled zoom, zoom-out lock, PMREM + RoomEnvironment, frameObject).
 *
 * BỔ SUNG so với bản gốc:
 *   1. setActive() — dừng/chạy rAF. BẮT BUỘC để không vượt trần WebGL context
 *      khi front-end mở nhiều iframe, và để không đốt pin khi iframe bị che.
 *   2. Timer của three thay Clock — xử lý tab ẩn đúng, dt clamp ≤ 0.1s.
 *   3. ResizeObserver thay window.resize — đúng cho iframe bị đổi kích thước.
 *   4. dispose() ĐẦY ĐỦ: scene content + render target + forceContextLoss.
 *      Bản gốc chỉ dispose controls + renderer → leak khi đổi tiết nhiều lần.
 *   5. onUpdate nhận (dt, elapsed) thay vì không tham số.
 *   6. setRenderFn() — cho PostFX thay chỗ renderer.render.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
// Timer đã nằm trong core từ three r168+ (không còn ở addons/misc/Timer.js)
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

import { disposeObject3D } from './dispose.js';
import { VIEW_PRESET } from './viewPreset.js';

/** @param {number} v @param {number} min @param {number} max */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Cho một mesh hiển thị ĐÚNG texture của nó, không nhận đèn — dùng cho vòm trời.
 *
 * Không thay vật liệu (thay là phải tự lo dispose + mất mọi thuộc tính khác), mà chuyển
 * texture màu sang kênh EMISSIVE và tắt màu khuếch tán: kết quả đúng bằng unlit, đảo
 * ngược được, và không cấp phát gì thêm.
 * @param {any} mesh
 */
function makeUnlit(mesh) {
  for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
    if (!m || !m.emissive || m.userData.__unlit) continue;
    m.emissiveMap = m.emissiveMap ?? m.map;
    m.emissive.set(0xffffff);
    m.emissiveIntensity = 1;
    if (m.color) m.color.set(0x000000);
    if ('envMapIntensity' in m) m.envMapIntensity = 0;
    m.userData.__unlit = true;
    m.needsUpdate = true;
  }
}

/** dt tối đa mỗi frame. Tab quay lại sau 5 phút không được cho dt = 300s. */
const MAX_DT = 0.1;

/**
 * @typedef {Object} EngineOptions
 * @property {number} [fov=42]
 * @property {number} [near=0.6]
 * @property {number} [far=100]
 * @property {[number, number, number]} [position=[0,1.2,3.2]]
 * @property {[number, number, number]} [target=[0,0,0]]
 * @property {number} [minPolarAngle]
 * @property {number} [maxPolarAngle]
 * @property {number} [dampingFactor=0.06]
 * @property {number} [rotateSpeed=0.3]
 * @property {number} [zoomSpeed=0.9]
 * @property {number} [panSpeed=0.6]
 * @property {boolean} [enablePan=false]
 * @property {number} [pixelRatioMax=2]
 * @property {boolean} [toneMapping=true]
 * @property {number} [exposure=1.0]  mặc định lấy từ VIEW_PRESET (khớp GLB.check)
 * @property {boolean} [shadows=true]
 *   Đổ bóng. Chỉ có tác dụng khi `presetLights` bật, vì đèn đổ bóng nằm trong bộ chuẩn.
 *   Tắt đi thì key light nhận lại đủ 0.8π ⇒ ảnh trùng khít gltf-viewer/GLB.check-không-bóng.
 * @property {number} [shadowMapSize=2048]
 * @property {boolean} [presetLights=true]
 *   true = Engine tự dựng bộ đèn chuẩn của VIEW_PRESET (ambient yếu + key light gắn vào
 *   camera). Tiết KHÔNG cần và KHÔNG NÊN tự thêm đèn: thêm nữa là lệch khỏi bản render
 *   dùng để duyệt trong GLB.check. Chỉ đặt false cho tiết có nhu cầu chiếu sáng riêng
 *   (vd mô phỏng ngày/đêm), và khi đó tiết tự chịu trách nhiệm toàn bộ ánh sáng.
 * @property {string} [clearColor='#0b0f14']
 * @property {number} [zoomSmooth=0.15]
 * @property {boolean} [lockZoomOut=true]  true = không zoom out xa hơn vị trí mặc định
 * @property {number} [minDistanceRatio=0.5]
 */

export class Engine {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {EngineOptions} [opts]
   */
  constructor(canvas, opts = {}) {
    if (!canvas) throw new Error('Engine: canvas không tồn tại');

    /** @type {Required<EngineOptions>} */
    this.config = /** @type {any} */ ({
      fov: 42,
      near: 0.6,
      far: 100,
      position: [0, 1.2, 3.2],
      target: [0, 0, 0],
      minPolarAngle: Math.PI * 0.2,
      maxPolarAngle: Math.PI * 0.8,
      dampingFactor: 0.06,
      rotateSpeed: 0.3,
      zoomSpeed: 0.9,
      panSpeed: 0.6,
      enablePan: false,
      pixelRatioMax: 2,
      toneMapping: true,
      exposure: VIEW_PRESET.exposure,
      presetLights: true,
      shadows: VIEW_PRESET.shadow.enabled,
      shadowMapSize: VIEW_PRESET.shadow.mapSize,
      clearColor: '#0b0f14',
      zoomSmooth: 0.15,
      lockZoomOut: true,
      minDistanceRatio: 0.5,
      ...opts,
    });

    this.canvas = canvas;

    /**
     * Hook: engine gọi khi setActive() đổi trạng thái. boot.js/debug panel gán.
     * @type {((active: boolean, reason: string) => void) | null}
     */
    this.onActiveChange = null;
    /**
     * Hook: engine gọi sau mỗi lần đổi kích thước. boot.js gán để chuyển tiếp
     * cho shell và cho lesson.onResize().
     * @type {((w: number, h: number) => void) | null}
     */
    this.onResize = null;
    /** @type {((e: WheelEvent) => void) | null} */
    this._onWheel = null;

    // ---------- Scene ----------
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.config.clearColor);

    // ---------- Camera ----------
    this.camera = new THREE.PerspectiveCamera(
      this.config.fov,
      1,
      this.config.near,
      this.config.far,
    );
    this.camera.position.set(...this.config.position);

    // ---------- Renderer ----------
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.config.pixelRatioMax));

    // ĐỔ BÓNG. Bật cùng lúc với bộ đèn chuẩn (đèn đổ bóng nằm trong bộ đó).
    //
    // autoUpdate = FALSE là điểm mấu chốt về hiệu năng: mặt trời đứng yên trong không
    // gian thế giới và model tĩnh, nên shadow map chỉ cần vẽ MỘT LẦN. Để autoUpdate bật
    // thì mỗi frame renderer vẽ lại toàn bộ caster — với cảnh 1.276 mesh là gấp đôi số
    // draw call cho một tấm ảnh không hề đổi. Tiết nào có vật đổ bóng chuyển động thì gọi
    // engine.invalidateShadows() ở frame đó.
    this._shadowsOn = this.config.shadows && this.config.presetLights;
    this.renderer.shadowMap.enabled = this._shadowsOn;
    // PCFShadowMap chứ KHÔNG phải PCFSoftShadowMap: từ r184 three đã khai tử loại "Soft"
    // — WebGLShadowMap.render() in cảnh báo rồi TỰ ÉP về PCFShadowMap ngay frame đầu.
    // Gán "Soft" chỉ tạo ảo giác là đã bật, thực tế vẫn PCF. Độ mềm của viền bóng lấy
    // bằng shadow.radius (đặt trong fitShadows).
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.shadowMap.autoUpdate = false;

    if (this.config.toneMapping) {
      // Chế độ có bóng dùng tone mapping NEUTRAL (chuẩn Khronos PBR): ACES kéo màu ấm về
      // xám và bóp bão hoà — chính là thứ làm cảnh sa mạc nhìn bạc. Chế độ không bóng giữ
      // ACES để trùng khít gltf-viewer.
      this.renderer.toneMapping = this._shadowsOn
        ? (THREE[`${VIEW_PRESET.shadow.toneMapping}ToneMapping`] ?? THREE.NeutralToneMapping)
        : THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = this.config.exposure;
    }
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // ---------- Environment (PBR, model không bị xỉn) ----------
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    /** @type {THREE.Texture} */
    this.envMap = pmrem.fromScene(new RoomEnvironment(), VIEW_PRESET.envBlur).texture;
    this.scene.environment = this.envMap;
    // IBL là nguồn sáng đều mọi phía — để nguyên 1.0 khi có bóng thì nó lấp đầy vùng bóng.
    this.scene.environmentIntensity = this._shadowsOn
      ? VIEW_PRESET.shadow.envIntensity
      : VIEW_PRESET.envIntensity;
    pmrem.dispose();

    // ---------- Controls ----------
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = this.config.dampingFactor;
    this.controls.rotateSpeed = this.config.rotateSpeed;
    this.controls.zoomSpeed = this.config.zoomSpeed;
    this.controls.panSpeed = this.config.panSpeed;
    this.controls.enablePan = this.config.enablePan;
    this.controls.minPolarAngle = this.config.minPolarAngle;
    this.controls.maxPolarAngle = this.config.maxPolarAngle;
    this.controls.target.set(...this.config.target);
    this._applyZoomLock();
    this.controls.update();

    // ---------- Ánh sáng: ĐÚNG bộ đèn của gltf-viewer/GLB.check ----------
    // Trước đây chỗ này là AmbientLight 1.25 + HemisphereLight 0.55 (ánh sáng đều mọi
    // phía). Đó là lý do model trông BẠC và DẸT so với ảnh duyệt trong GLB.check: khối
    // lồi không có mặt sáng/mặt tối, và tiết nào cũng phải tự thêm đèn đè lên → tổng
    // ambient vọt lên ~1.55 và cháy sáng. Nay Engine dựng đúng một bộ, tiết không thêm.
    /** @type {THREE.AmbientLight | THREE.HemisphereLight | null} */
    this._ambient = null;
    /** @type {THREE.DirectionalLight | null} */
    this._key = null;
    /** @type {THREE.Object3D | null} */
    this._keyTarget = null;
    /** @type {THREE.DirectionalLight | null} */
    this._sun = null;
    /**
     * Kết quả canh vùng đổ bóng của fitShadows: tâm · bán kính · khoảng cách đặt mặt
     * trời. Giữ lại để đặt lại mặt trời mỗi khi camera xoay mà KHÔNG phải duyệt lại cả
     * cây model (duyệt 8 triệu tam giác mỗi frame thì không có gì chạy nổi).
     * @type {{center: THREE.Vector3, radius: number, dist: number} | null}
     */
    this._shadowFit = null;
    /** Hướng mặt trời đang áp dụng — để biết camera đã xoay đủ xa chưa. @type {THREE.Vector3 | null} */
    this._sunDir = null;
    /** @type {THREE.Object3D | null} */
    this._sunTarget = null;
    if (this.config.presetLights) {
      // Có bóng thì dùng HEMISPHERE thay AmbientLight: trời xanh hắt xuống vùng bóng,
      // cát hắt ngược lên. Cặp ấm–lạnh này mới là thứ làm ảnh ngoài trời "lên màu";
      // ambient trắng phẳng chỉ làm nhạt màu và xoá khối.
      const sh = VIEW_PRESET.shadow;
      this._ambient = this._shadowsOn
        ? new THREE.HemisphereLight(sh.hemi.sky, sh.hemi.ground, sh.hemi.intensity)
        : new THREE.AmbientLight(VIEW_PRESET.ambient.color, VIEW_PRESET.ambient.intensity);
      this._ambient.name = 'preset_ambient';
      this.scene.add(this._ambient);

      // Có bóng: key chỉ còn là fill nhẹ (mặt trời gánh phần chính). Không bóng: đủ 0.8π.
      const keyI = this._shadowsOn ? sh.keyIntensity : VIEW_PRESET.key.intensity;
      this._key = new THREE.DirectionalLight(VIEW_PRESET.key.color, keyI);
      this._key.name = 'preset_key';
      this._key.position.set(...VIEW_PRESET.key.position);
      // Gắn vào CAMERA: đèn đi theo góc nhìn, xoay tới đâu mặt hướng về người xem cũng
      // được chiếu. Đây là cách gltf-viewer làm.
      this.camera.add(this._key);

      // HƯỚNG của DirectionalLight = (vị trí đèn − vị trí target). Mặc định target là một
      // Object3D đứng ở GỐC TOẠ ĐỘ THẾ GIỚI. gltf-viewer dời model về gốc nên "gốc thế
      // giới" chính là tâm model → đèn rọi gần như dọc theo hướng nhìn. Bài giảng thì
      // GIỮ NGUYÊN toạ độ gốc của model (tiết his-06 cố ý, vì khoảng cách camera ghi
      // trong model theo đơn vị model), nên nếu để mặc định thì đèn rọi về một điểm chẳng
      // liên quan và ảnh KHÁC bản duyệt trong GLB.check.
      // Cho target bám TÂM QUAY (controls.target) là tái lập đúng hành vi của gltf-viewer
      // mà không phụ thuộc model nằm ở đâu trong không gian.
      this._keyTarget = new THREE.Object3D();
      this._keyTarget.name = 'preset_key_target';
      this.scene.add(this._keyTarget);
      this._key.target = this._keyTarget;

      // MẶT TRỜI — đèn thứ hai, ĐỨNG YÊN trong không gian thế giới, là nguồn duy nhất đổ
      // bóng. Vị trí thật và khung shadow camera do fitShadows() đặt theo kích thước model.
      if (this._shadowsOn) {
        this._sun = new THREE.DirectionalLight(sh.sun.color, sh.sun.intensity);
        this._sun.name = 'preset_sun';
        this._sun.castShadow = true;
        this._sun.shadow.mapSize.set(this.config.shadowMapSize, this.config.shadowMapSize);
        this._sunTarget = new THREE.Object3D();
        this._sunTarget.name = 'preset_sun_target';
        this._sun.target = this._sunTarget;
        this.scene.add(this._sun, this._sunTarget);
      }
    }
    // Camera phải nằm trong scene graph, nếu không con của nó (đèn) không được cập nhật
    // matrixWorld. Thêm vô điều kiện để tiết gắn thêm gì vào camera cũng chạy.
    this.scene.add(this.camera);

    // ---------- Smooth zoom state ----------
    /** @type {number | null} */
    this._zoomTargetDistance = null;
    /**
     * Vật thể đang được canh khung + padding, để canh LẠI khi aspect đổi.
     * Cần vì frameObject() phụ thuộc aspect, mà nó thường được gọi trong
     * lesson.init() — có thể TRƯỚC lần _resize() đầu tiên, lúc aspect còn là 1.
     * Cũng xử lý luôn việc quay ngang/dọc điện thoại và iframe bị đổi kích thước.
     * @type {{ object: THREE.Object3D, padding: number } | null}
     */
    this._framed = null;
    /** Người dùng đã tự zoom chưa — nếu rồi thì resize KHÔNG canh lại đè lên. */
    this._userZoomed = false;
    this._zoomEps = 0.001;
    this._tmpDir = new THREE.Vector3();
    this._tmpDir2 = new THREE.Vector3();

    // ---------- Loop ----------
    this.timer = new THREE.Timer();
    /** @type {Array<(dt: number, elapsed: number) => void>} */
    this._updates = [];
    /** @type {(() => void) | null} */
    this._renderFn = null;
    this._active = true;
    this._running = false;
    this._rafId = 0;
    this.frameCount = 0;

    // ---------- Resize: ResizeObserver, đúng cho iframe ----------
    this._resize = this._resize.bind(this);
    this._ro = new ResizeObserver(this._resize);
    this._ro.observe(canvas.parentElement ?? canvas);
    this._resize();

    // ---------- Tab ẩn → tự dừng ----------
    this._onVisibility = () => this.setActive(document.visibilityState === 'visible', 'tab-hidden');
    document.addEventListener('visibilitychange', this._onVisibility);

    this._tick = this._tick.bind(this);
    this.enableWheelZoomSmoothing(true);
    this.start();
  }

  // =========================================================================
  // Vòng lặp
  // =========================================================================

  /** Bắt đầu rAF. boot.js gọi; dev hầu như không cần. */
  start() {
    if (this._running) return;
    this._running = true;
    this.timer.reset();
    this._rafId = requestAnimationFrame(this._tick);
  }

  /** Dừng rAF hoàn toàn. */
  stop() {
    this._running = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = 0;
  }

  /**
   * Bật/tắt render. Đây là cơ chế chống vượt trần WebGL context.
   * @param {boolean} active
   * @param {'offscreen'|'tab-hidden'|'user'} [reason]
   */
  setActive(active, reason = 'user') {
    if (this._active === active) return;
    this._active = active;
    if (active) {
      this.timer.reset(); // không để dt nhảy vọt sau khi ngủ
      this.start();
    } else {
      this.stop();
    }
    this.onActiveChange?.(active, reason);
  }

  /** @returns {boolean} */
  isActive() {
    return this._active;
  }

  /**
   * Đăng ký hàm chạy mỗi frame.
   * @param {(dt: number, elapsed: number) => void} fn
   * @returns {() => void} hàm gỡ
   */
  onUpdate(fn) {
    if (typeof fn !== 'function') return () => {};
    this._updates.push(fn);
    return () => {
      const i = this._updates.indexOf(fn);
      if (i >= 0) this._updates.splice(i, 1);
    };
  }

  /**
   * Thay hàm render (dùng khi bật PostFX composer).
   * @param {(() => void) | null} fn
   */
  setRenderFn(fn) {
    this._renderFn = fn;
  }

  /** Render đúng 1 frame ngay lập tức, không qua rAF. Dùng cho smoke test. */
  renderOnce() {
    this.timer.update();
    const dt = Math.min(this.timer.getDelta(), MAX_DT);
    const elapsed = this.timer.getElapsed();
    for (const fn of this._updates) fn(dt, elapsed);
    this.controls.update();
    this._stepZoom();
    this._draw();
    this.frameCount++;
  }

  _tick() {
    if (!this._running) return;
    this._rafId = requestAnimationFrame(this._tick);

    this.timer.update();
    const dt = Math.min(this.timer.getDelta(), MAX_DT);
    const elapsed = this.timer.getElapsed();

    for (const fn of this._updates) fn(dt, elapsed);

    this.controls.update();
    this._stepZoom();
    this._draw();
    this.frameCount++;
  }

  _draw() {
    // Target của key light bám tâm quay — xem lý do ở chỗ dựng đèn trong constructor.
    // Đặt ở đây (một chỗ duy nhất mọi lần vẽ đều đi qua) thay vì trong _tick, để cả
    // đường vẽ lại sau resize và PostFX cũng đúng.
    if (this._keyTarget) this._keyTarget.position.copy(this.controls.target);
    this._syncSunToCamera();
    if (this._renderFn) this._renderFn();
    else this.renderer.render(this.scene, this.camera);
  }

  // =========================================================================
  // Zoom
  // =========================================================================

  _applyZoomLock() {
    const d = this.camera.position.distanceTo(this.controls.target);
    if (this.config.lockZoomOut) this.controls.maxDistance = d;
    this.controls.minDistance = d * this.config.minDistanceRatio;
  }

  _stepZoom() {
    if (this._zoomTargetDistance === null) return;
    const target = this.controls.target;
    const cur = this.camera.position.distanceTo(target);
    const next = THREE.MathUtils.lerp(cur, this._zoomTargetDistance, this.config.zoomSmooth);
    this._tmpDir.subVectors(this.camera.position, target).normalize();
    this.camera.position.copy(target).addScaledVector(this._tmpDir, next);
    if (Math.abs(next - this._zoomTargetDistance) < this._zoomEps) this._zoomTargetDistance = null;
  }

  /** @param {number} fov */
  setFov(fov) {
    this.camera.fov = clamp(fov, 20, 90);
    this.camera.updateProjectionMatrix();
  }

  /**
   * Zoom mượt tới khoảng cách. Đây là ĐƯỜNG DUY NHẤT người dùng zoom (lăn chuột và
   * thanh zoom dọc đều gọi hàm này), nên cũng là chỗ đánh dấu "người dùng đã tự zoom"
   * để _resize() không canh khung lại đè lên lựa chọn của họ.
   * @param {number} dist
   */
  setZoomTargetDistance(dist) {
    this._userZoomed = true;
    this._zoomTargetDistance = clamp(dist, this.controls.minDistance, this.controls.maxDistance);
  }

  /**
   * HUỶ hành trình zoom mượt đang chạy dở.
   *
   * Vì sao cần API riêng: setZoomTargetDistance() chỉ ghi đích, còn _stepZoom() lerp dần
   * tới đó ở MỖI frame cho tới khi chạm. Nếu tiết tự đặt lại điểm nhìn (bay sang bước
   * khác) trong lúc hành trình zoom chưa xong thì hai bên giành nhau camera: _stepZoom()
   * chạy SAU update() của tiết nên nó thắng, và bước mới đáp sai khoảng cách.
   * Đo được: đang ở 16.82, lăn chuột về 9 rồi bấm ngay sang bước có khoảng cách 2.79 →
   * camera dừng ở 5.61. Gọi hàm này trước khi đặt điểm nhìn là hết.
   */
  cancelZoom() {
    this._zoomTargetDistance = null;
  }

  /** Đặt khoảng cách NGAY, không mượt. Dùng khi reset/frameObject. @param {number} dist */
  setDistance(dist) {
    const d = clamp(dist, this.controls.minDistance, this.controls.maxDistance);
    const target = this.controls.target;
    this._tmpDir2.subVectors(this.camera.position, target).normalize();
    this.camera.position.copy(target).addScaledVector(this._tmpDir2, d);
    this.controls.update();
  }

  /** Về đúng góc nhìn ban đầu. Nút "Đặt lại" của taskbar gọi hàm này. */
  resetView() {
    this.camera.fov = this.config.fov;
    this.camera.near = this.config.near;
    this.camera.far = this.config.far;
    this.camera.updateProjectionMatrix();
    this.controls.target.set(...this.config.target);
    this.camera.position.set(...this.config.position);
    this._applyZoomLock();
    this._zoomTargetDistance = null;
    this.controls.update();
  }

  /**
   * Khoảng cách để vật thể vừa khung, + tâm bounding box.
   * Tách khỏi frameObject() để _resize() canh lại được mà không nhân bản công thức.
   * @param {THREE.Object3D} object3D
   * @param {number} padding
   */
  _fitDistance(object3D, padding) {
    const box = new THREE.Box3().setFromObject(object3D);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    // Xét RIÊNG hai ràng buộc rồi lấy khoảng cách lớn hơn:
    //   · CHIỀU CAO  → fov dọc
    //   · CHIỀU NGANG → fov dọc × aspect
    // Công thức cũ fit chiều LỚN NHẤT vào fov DỌC, sai theo cả hai hướng: vật thể
    // rộng-thấp (bản đồ phẳng 3.45 × 1.73) bị lùi xa gấp ~1.8 lần mức cần nên hiện ra
    // bé xíu; còn trên màn DỌC (aspect < 1) thì ngược lại — quả cầu bị cắt hai bên.
    //
    // Chiều ngang dùng ĐƯỜNG CHÉO MẶT X–Z, không phải size.x: OrbitControls cho xoay
    // quanh trục Y, nên khi người dùng xoay 45° thì bề ngang nhìn thấy là đường chéo
    // đó. Với khối có bề dày thật (model nước 2.4 × 1.67 × 2.36) mà chỉ tính size.x là
    // canh quá gần và khối bị cắt ngay khi xoay.
    // KHÔNG cộng thêm nửa bề dày: với hình cầu size.z = đường kính, cộng vào là lùi
    // thừa cả một bán kính. `padding` đã là chỗ chừa lề — đừng chừa hai lần.
    const halfFov = THREE.MathUtils.degToRad(this.camera.fov) / 2;
    const aspect = this.camera.aspect || 1;
    const widthWorst = Math.hypot(size.x, size.z);
    const fitH = size.y / 2 / Math.tan(halfFov);
    const fitW = widthWorst / 2 / (Math.tan(halfFov) * aspect);
    return { dist: Math.max(fitH, fitW, 0.001) * padding, center };
  }

  /**
   * Đưa camera tới khoảng cách vừa khít bounding box của object.
   * GHI NHỚ vật thể + padding để canh lại khi aspect đổi — xem this._framed.
   * @param {THREE.Object3D} object3D
   * @param {number} [padding=1.25]
   */
  frameObject(object3D, padding = 1.25) {
    this._framed = { object: object3D, padding };
    this._userZoomed = false;
    this._applyFit(object3D, padding);
  }

  /** @param {THREE.Object3D} object3D @param {number} padding */
  _applyFit(object3D, padding) {
    const { dist, center } = this._fitDistance(object3D, padding);

    this.controls.target.copy(center);
    // setDistance() kẹp vào [minDistance, maxDistance] → PHẢI nới CẢ HAI biên trước,
    // không thì dist bị kẹp và khung ra sai.
    // Trước đây chỉ nới maxDistance: với model nhỏ (bbox 0.32) và minDistance cũ 0.85
    // từ lần _applyZoomLock trước, fitDist 0.48 bị kẹp lên 0.85 → camera đứng xa gấp
    // ~3.5 lần và vật thể chỉ còn bằng hạt đỗ giữa màn hình.
    this.controls.maxDistance = Math.max(this.controls.maxDistance, dist);
    this.controls.minDistance = Math.min(this.controls.minDistance, dist);
    this.setDistance(dist);
    this._applyZoomLock();
    this.controls.update();
  }

  /**
   * Wheel zoom mượt: normalize deltaMode + scale theo khoảng cách hiện tại.
   * Đây là phần kinh nghiệm từ three-lessons, giữ nguyên logic.
   * @param {boolean} [enabled=true]
   */
  enableWheelZoomSmoothing(enabled = true) {
    if (enabled) {
      this.controls.enableZoom = false;
      if (this._onWheel) return;
      this._onWheel = (/** @type {WheelEvent} */ e) => {
        if (e.target !== this.renderer.domElement) return;
        e.preventDefault();

        const cur = this.camera.position.distanceTo(this.controls.target);
        let dy = e.deltaY;
        if (e.deltaMode === 1)
          dy *= 16; // line → px
        else if (e.deltaMode === 2) dy *= 400; // page → px

        const sensitivity = 0.0022;
        const distScale = Math.max(cur, 1.0); // xa thì zoom mạnh hơn, gần thì nhẹ
        this.setZoomTargetDistance(cur + dy * sensitivity * distScale);
      };
      this.renderer.domElement.addEventListener('wheel', this._onWheel, { passive: false });
    } else {
      this.controls.enableZoom = true;
      if (this._onWheel) {
        this.renderer.domElement.removeEventListener('wheel', this._onWheel);
        this._onWheel = null;
      }
    }
  }

  // =========================================================================
  // Tiện ích vật liệu (giữ từ three-lessons)
  // =========================================================================

  /** @param {THREE.Object3D} root @param {number} [intensity=1] */
  applyEnvIntensity(root, intensity = 1.0) {
    root.traverse((/** @type {THREE.Object3D} */ o) => {
      const any = /** @type {any} */ (o);
      if (!any.isMesh || !any.material) return;
      for (const m of Array.isArray(any.material) ? any.material : [any.material]) {
        if (m && 'envMapIntensity' in m) {
          m.envMapIntensity = intensity;
          m.needsUpdate = true;
        }
      }
    });
  }

  /** Làm hết bóng — dùng khi model bị highlight gắt.
   * @param {THREE.Object3D} root @param {number} [roughness=1] @param {number} [metalness=0] */
  applyNoSpecular(root, roughness = 1.0, metalness = 0.0) {
    root.traverse((/** @type {THREE.Object3D} */ o) => {
      const any = /** @type {any} */ (o);
      if (!any.isMesh || !any.material) return;
      for (const m of Array.isArray(any.material) ? any.material : [any.material]) {
        if (!m) continue;
        if ('roughness' in m) m.roughness = roughness;
        if ('metalness' in m) m.metalness = metalness;
        m.needsUpdate = true;
      }
    });
  }

  /**
   * Đặt độ mờ cho CẢ CÂY object. Dùng cho hiệu ứng chuyển cảnh dạng "một mô hình tan
   * ra, mô hình kia hiện lên" (lối của Mozaik 3D / bản three-lessons cũ).
   *
   * Ba điểm không hiển nhiên:
   *
   * 1. PHẢI cache trạng thái gốc rồi TRẢ LẠI khi opacity về 1. Bỏ `transparent: true`
   *    nằm lại trên một material vốn đặc thì màu bị bạc và thứ tự vẽ sai vĩnh viễn.
   *    Đây là lý do có nhánh `>= 0.999` riêng thay vì chỉ gán opacity = 1.
   *
   * 2. KHÔNG đụng vào depthWrite. Bản three-lessons cũ tắt nó (depthWrite: false) trong
   *    lúc fade, nhưng với mô hình ĐẶC thì làm vậy sai hai đường:
   *      · đang fade thì thấy xuyên qua vật, mặt sau lộ ra qua mặt trước;
   *      · và tệ hơn: lúc opacity về 1 phải trả depthWrite lại true, tức ĐỔI CÁCH SẮP
   *        XẾP ĐỘ SÂU ngay bước cuối → silhouette snap một cái. Đúng chỗ "phật".
   *    Giữ depthWrite nguyên giá trị gốc thì lớp che nhau vẫn đúng suốt nhịp fade và
   *    không có bước nhảy nào ở cuối. (Chỉ hợp lý vì đây là mô hình đặc; hình trong
   *    suốt nhiều lớp thật thì lại cần tắt.)
   *
   * 3. ShaderMaterial (quầng khí quyển) BỎ QUA material.opacity hoàn toàn — phải nhân
   *    vào uniform uOpacity của chính nó. Thiếu nhánh này thì quả cầu tan hết mà vành
   *    sáng xanh vẫn còn nguyên, lơ lửng giữa nền.
   *
   * @param {THREE.Object3D | null | undefined} root
   * @param {number} opacity 0..1
   */
  setObjectOpacity(root, opacity) {
    if (!root) return;
    root.traverse((/** @type {THREE.Object3D} */ o) => {
      const any = /** @type {any} */ (o);
      if (!any.isMesh || !any.material) return;
      for (const m of Array.isArray(any.material) ? any.material : [any.material]) {
        if (!m) continue;
        const uOpacity = m.uniforms?.uOpacity;
        if (!m.userData.__fade) {
          m.userData.__fade = {
            transparent: m.transparent,
            opacity: m.opacity,
            uOpacity: uOpacity ? uOpacity.value : null,
          };
        }
        const org = m.userData.__fade;
        if (opacity >= 0.999) {
          m.transparent = org.transparent;
          m.opacity = org.opacity;
          if (uOpacity && org.uOpacity !== null) uOpacity.value = org.uOpacity;
          continue;
        }
        m.transparent = true;
        m.opacity = opacity;
        if (uOpacity && org.uOpacity !== null) uOpacity.value = org.uOpacity * opacity;
      }
    });
  }

  /**
   * Chờ engine vẽ xong `n` frame, hoặc bỏ qua sau `timeoutMs`.
   * @param {number} n @param {number} timeoutMs @returns {Promise<void>}
   */
  waitFrames(n, timeoutMs) {
    const target = this.frameCount + n;
    /** @type {Promise<void>} */
    const p = new Promise((resolve) => {
      let done = false;
      const fin = () => {
        if (done) return;
        done = true;
        resolve(undefined);
      };
      const tick = () => {
        if (done) return;
        if (this.frameCount >= target) fin();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      // Chốt: rAF có thể KHÔNG chạy lần nào (tab ẩn, engine tạm dừng) nên vòng trên
      // không bao giờ tự thoát được.
      setTimeout(fin, timeoutMs);
    });
    return p;
  }

  /**
   * Tween độ mờ của một object từ `from` tới `to` trong `ms`.
   *
   * HÂM NÓNG trước nhịp HIỆN LÊN — đây là phần quan trọng nhất của hàm này.
   * Frame ĐẦU TIÊN của một object vừa được bật `visible` rất đắt: three phải biên dịch
   * shader program và đẩy texture lên GPU (model.water 0,59 MB, bản đồ 2D 2048×1024).
   * Frame đó có thể kéo 100–300ms. Nếu tween bắt đầu ngay thì bước đầu của nó nhảy một
   * nhát lớn — trông đúng như "phật một cái" rồi mới mượt.
   * Nên: đặt opacity = from (0) → compile() → chờ 2 frame ĐÃ VẼ → mới tween. Toàn bộ
   * chi phí frame đầu diễn ra lúc object còn trong suốt, không ai thấy.
   * (Vẽ ở opacity 0 vẫn phát draw call thật, nên texture vẫn được upload trong lúc đó.)
   *
   * Có CHỐT THỜI GIAN THỰC: nếu requestAnimationFrame ngừng chạy giữa nhịp (tab bị ẩn,
   * engine tạm dừng) thì setTimeout vẫn nhảy tới trạng thái cuối và resolve. Thiếu nó,
   * mô hình đứng lại ở nửa mờ vĩnh viễn và promise không bao giờ về.
   *
   * @param {THREE.Object3D | null | undefined} root
   * @param {number} from @param {number} to @param {number} ms
   * @returns {Promise<void>}
   */
  async fadeObject(root, from, to, ms) {
    if (!root) return;
    this.setObjectOpacity(root, from);

    // Token để nhịp fade mới huỷ nhịp cũ trên CÙNG object — bấm đổi bước liên tục thì
    // hai tween sẽ ghi chồng lên nhau và độ mờ nhảy loạn.
    const token = {};
    const rootAny = /** @type {any} */ (root);
    rootAny.userData.__fadeToken = token;

    if (to > from) {
      // compile() dựng sẵn shader program cho mọi material trong scene. Gọi nhiều lần
      // vô hại: lần sau three thấy program đã có thì trả về ngay.
      try {
        this.renderer.compile(this.scene, this.camera);
      } catch {
        // Không chặn hiệu ứng nếu compile() lỗi trên driver nào đó.
      }
      await this.waitFrames(2, 400);
      if (rootAny.userData.__fadeToken !== token) return;
    }

    const t0 = performance.now();
    /** @type {Promise<void>} */
    const p = new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        if (rootAny.userData.__fadeToken === token) this.setObjectOpacity(root, to);
        resolve(undefined);
      };
      const tick = () => {
        if (done) return;
        if (rootAny.userData.__fadeToken !== token) return resolve(undefined);
        const t = Math.min((performance.now() - t0) / ms, 1);
        // easeOutSine cho CẢ HAI chiều: đi nhanh ở đầu rồi tắt dần.
        // KHÔNG dùng easeInOut (bản trước): nó gần như phẳng ở ~15% đầu, mắt không thấy
        // gì rồi vật vọt qua vùng giữa — đọc ra thành "phật một cái" chứ không phải hiện
        // dần. Ease-out cho vật có mặt mờ mờ ngay lập tức rồi đậm lên êm, và ở chiều
        // ngược lại thì nhạt đi ngay chứ không nán lại rồi rơi đột ngột ở cuối.
        const e = Math.sin((Math.PI / 2) * t);
        this.setObjectOpacity(root, from + (to - from) * e);
        if (t < 1) requestAnimationFrame(tick);
        else finish();
      };
      requestAnimationFrame(tick);
      setTimeout(finish, ms + 250);
    });
    await p;
  }

  // =========================================================================
  // Đổ bóng
  // =========================================================================

  /**
   * Đặt vùng đổ bóng theo model: chọn mesh nào đổ bóng, canh shadow camera vừa khít, rồi
   * vẽ shadow map MỘT LẦN. Tiết gọi ngay sau khi thêm model vào scene.
   *
   * Vì sao phải gọi tay thay vì Engine tự làm: Engine không có hook nào biết "model đã
   * nạp xong". Canh sai khung là hỏng hẳn — khung quá rộng thì bóng vỡ hạt (mỗi texel
   * phủ vài mét), quá hẹp thì bóng bị cắt cụt giữa cảnh.
   *
   * @param {THREE.Object3D} object gốc cây model
   */
  fitShadows(object) {
    if (!this._sun || !this._sunTarget || !object) return;
    const sh = VIEW_PRESET.shadow;

    const rootSphere = new THREE.Box3().setFromObject(object).getBoundingSphere(new THREE.Sphere());
    const maxCaster = rootSphere.radius * sh.casterMaxRatio;

    // Mesh nào đổ bóng: mọi thứ TRỪ vật quá lớn (vòm trời). Nhận bóng thì tất cả.
    const casters = new THREE.Box3();
    const tmp = new THREE.Box3();
    const skyMin = rootSphere.radius * sh.skyDomeRatio;
    object.traverse((/** @type {any} */ n) => {
      if (!n.isMesh) return;
      n.receiveShadow = true;
      tmp.setFromObject(n);
      const r = tmp.isEmpty() ? 0 : tmp.getBoundingSphere(new THREE.Sphere()).radius;
      n.castShadow = r > 0 && r <= maxCaster;
      if (n.castShadow) casters.union(tmp);
      // Vòm trời: to bằng cả model VÀ bao lấy tâm model.
      else if (r >= skyMin && tmp.containsPoint(rootSphere.center)) makeUnlit(n);
    });
    if (casters.isEmpty()) return;

    const s = casters.getBoundingSphere(new THREE.Sphere());
    const dist = s.radius * sh.distanceFactor;
    this._shadowFit = { center: s.center.clone(), radius: s.radius, dist };
    this._placeSun(this._sunDirection());

    const cam = /** @type {THREE.OrthographicCamera} */ (this._sun.shadow.camera);
    cam.left = -s.radius;
    cam.right = s.radius;
    cam.top = s.radius;
    cam.bottom = -s.radius;
    cam.near = Math.max(0.01, dist - s.radius * 1.5);
    cam.far = dist + s.radius * 1.5;
    cam.updateProjectionMatrix();

    // normalBias quy từ SỐ TEXEL sang đơn vị model — xem VIEW_PRESET.shadow.normalBiasTexels.
    const texel = (s.radius * 2) / this._sun.shadow.mapSize.x;
    this._sun.shadow.normalBias = texel * sh.normalBiasTexels;
    this._sun.shadow.bias = sh.bias;
    // Viền bóng mềm: PCF lấy mẫu rộng ra `radius` texel. Bóng cứng đanh nhìn như dán
    // decal, còn viền hơi nhoè mới giống bóng ngoài trời.
    this._sun.shadow.radius = sh.softness;

    this.invalidateShadows();
  }

  /**
   * Cường độ ÁNH SÁNG MÔI TRƯỜNG (PMREM của RoomEnvironment).
   *
   * Tách hẳn khỏi bộ đèn preset: `setPresetLightsEnabled(false)` chỉ ẩn ba đèn, còn
   * `scene.environment` vẫn nguyên. Đó là CHỦ Ý — glTF không chở được World của Blender,
   * nên khi dùng đèn của model thì lớp môi trường trung tính này là thứ duy nhất còn lấp
   * vùng khuất sáng, không có nó thì mặt quay lưng đèn đen kịt.
   * @param {number} v 0 = tắt hẳn
   */
  setEnvironmentIntensity(v) {
    this.scene.environmentIntensity = Math.max(0, v);
  }

  /**
   * Bật/tắt BỘ ĐÈN CỦA NỀN TẢNG lúc chạy.
   *
   * Dùng khi model tự MANG THEO đèn (`KHR_lights_punctual`): lúc đó chồng thêm bộ preset
   * là cộng dồn hai hệ thống sáng, ảnh cháy trắng và không còn giống thứ người dựng thấy
   * trong Blender. Không xoá đèn mà chỉ ẩn — bật lại được, và không phải dispose gì.
   *
   * Mặt trời tắt theo thì bóng cũng mất; đèn của model tự lo phần đó (đèn nào muốn đổ
   * bóng phải tự bật `castShadow`, xem markModelLightsCastShadow()).
   *
   * `keepAmbient` — GIỮ LẠI đèn nền (hemisphere/ambient) kể cả khi tắt bộ preset. Đây là
   * mặc định khi dùng đèn của model, và có lý do cấu trúc: glTF chở được đèn punctual
   * nhưng KHÔNG BAO GIỜ chở được World của Blender. Model vì thế luôn thiếu đúng phần
   * ánh sáng nền mà người dựng nhìn thấy trong Blender. Hemisphere của nền tảng là thứ
   * thay chỗ đó; tắt nốt nó đi thì vùng khuất nắng đen kịt.
   * Chỉ hai đèn HƯỚNG (key, sun) mới phải tắt — chúng chồng lên đèn hướng của model.
   * @param {boolean} on
   * @param {{keepAmbient?: boolean}} [opts]
   */
  setPresetLightsEnabled(on, opts = {}) {
    for (const l of [this._key, this._sun]) {
      if (l) l.visible = on;
    }
    if (this._ambient) this._ambient.visible = on || Boolean(opts.keepAmbient);
    this.invalidateShadows();
  }

  /**
   * Áp TOÀN BỘ chính sách đèn-của-model cho một cây model. Gọi một lần sau khi nạp.
   *
   * Gộp bốn việc vào một chỗ vì chúng phải xảy ra ĐÚNG THỨ TỰ và luôn đi cùng nhau:
   *   1. dò xem model có mang đèn không
   *   2. tắt hai đèn HƯỚNG của preset (giữ đèn nền — glTF không chở được World)
   *   3. quy đổi đơn vị cường độ (bộ xuất Blender nhân 683)
   *   4. bật đổ bóng cho đèn model
   * Tách rời ra thì mỗi tiết lại gọi thiếu một bước, và ánh sáng mỗi bài một khác.
   *
   * Bộ số lấy từ `VIEW_PRESET.modelLights` — GIỐNG NHAU cho mọi model. Tiết KHÔNG có
   * khoá nào để đè lên; model sáng/tối lệch thì sửa ở model.
   * @param {THREE.Object3D} root gốc cây model
   * @returns {{source: 'model'|'preset', lights: number, shadowLights: number,
   *   before: number[], after: number[], types: string}}
   */
  applyModelLights(root) {
    const cfg = VIEW_PRESET.modelLights;
    /** @type {any[]} */
    const found = [];
    root?.traverse((/** @type {any} */ o) => {
      if (o.isLight) found.push(o);
    });
    const use = cfg.mode === 'model' || (cfg.mode === 'auto' && found.length > 0);
    const ket = {
      source: /** @type {'model'|'preset'} */ (use ? 'model' : 'preset'),
      lights: found.length,
      shadowLights: 0,
      /** @type {number[]} */ before: [],
      /** @type {number[]} */ after: [],
      types: [...new Set(found.map((l) => l.type))].join(','),
    };
    if (!use) return ket;

    this.setPresetLightsEnabled(false, { keepAmbient: cfg.keepAmbient });
    // Quy đổi TRƯỚC mọi thứ khác: số trong file là đơn vị quang học của glTF
    // (lux/candela), lớn gấp hàng trăm lần thang mà preset đang chạy.
    const n = this.normalizeModelLights(root, {
      unit: /** @type {'blender'|'gltf'} */ (cfg.unit),
      scale: cfg.scale,
    });
    ket.before = n.before;
    ket.after = n.after;
    if (cfg.castShadow) {
      ket.shadowLights = this.markModelLightsCastShadow(root, {
        mode: cfg.castShadow === 'all' ? 'all' : 'directional',
        max: cfg.shadowMax,
      });
    }
    if (cfg.envIntensity != null) this.setEnvironmentIntensity(cfg.envIntensity);
    return ket;
  }

  /**
   * Kẹp các thông số vật liệu về khoảng HỢP LỆ của chuẩn glTF.
   *
   * Blender cho nhập roughness 5.0 hay specular tint [2,2,2]; bộ xuất chép thẳng con số
   * đó vào file dù chuẩn glTF quy định [0,1]. three.js không kiểm, nó đưa nguyên vào
   * shader — kết quả là tô bóng vô nghĩa và viền sáng cháy trắng. Đo trên model Giza:
   * `Maldivian Cement Brick Wall` có roughnessFactor = 5, hai vật liệu `Stone Wall` có
   * specularColorFactor = [2,2,2].
   *
   * Kẹp về [0,1] KHÔNG phải là đổi thẩm mỹ — đó là ép về đúng miền giá trị mà chuẩn định
   * nghĩa. Giá trị ngoài miền vốn không có ý nghĩa xác định.
   * @param {THREE.Object3D} root
   * @returns {{fixed: number, details: string[]}}
   */
  sanitizeMaterials(root) {
    /** Thuộc tính vô hướng phải nằm trong [0,1] theo chuẩn. */
    const SCALARS = [
      'roughness',
      'metalness',
      'clearcoat',
      'clearcoatRoughness',
      'transmission',
      'sheen',
      'sheenRoughness',
      'iridescence',
      'specularIntensity',
      'opacity',
    ];
    /** @type {string[]} */
    const details = [];
    let fixed = 0;
    const seen = new Set();
    root.traverse((/** @type {any} */ o) => {
      if (!o.isMesh || !o.material) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        if (!m || seen.has(m.uuid)) continue;
        seen.add(m.uuid);
        for (const k of SCALARS) {
          const v = m[k];
          if (typeof v !== 'number' || (v >= 0 && v <= 1)) continue;
          details.push(`${m.name || '(không tên)'}.${k} ${v} → ${Math.min(1, Math.max(0, v))}`);
          m[k] = Math.min(1, Math.max(0, v));
          fixed++;
        }
        // specularColor là màu, ba kênh cũng phải trong [0,1].
        const sc = m.specularColor;
        if (sc && (sc.r > 1 || sc.g > 1 || sc.b > 1)) {
          details.push(
            `${m.name || '(không tên)'}.specularColor [${sc.r}, ${sc.g}, ${sc.b}] → kẹp về 1`,
          );
          sc.setRGB(Math.min(1, sc.r), Math.min(1, sc.g), Math.min(1, sc.b));
          fixed++;
        }
      }
    });
    return { fixed, details };
  }

  /**
   * Quy CƯỜNG ĐỘ đèn của model về thang mà bộ số của nền tảng đang dùng.
   *
   * Bộ xuất glTF của Blender nhân cường độ với **683** (hệ số W → lumen) để ra đơn vị
   * quang học của glTF: lux cho directional, candela cho point/spot. Con số vì thế nhảy
   * lên hàng nghìn — đo trên model thật: Sun Strength 4.0 trong Blender ⇒ `2732 lux`
   * trong file. three.js nhận thẳng số đó làm hệ số nhân, mà preset của dự án đang chạy
   * mặt trời ở 6.0 với exposure 1.0, nên 2732 là sáng gấp ~450 lần: ảnh cháy trắng.
   *
   * Hàm này làm ĐÚNG PHÉP NGƯỢC LẠI của bộ xuất, đưa cường độ về lại con số người dựng
   * gõ trong Blender:
   *   directional : lux / 683                → W/m²
   *   point, spot : candela × 4π / 683       → W
   * Sun 4.0 trong Blender ra 4.0 trong three — cùng thang với mặt trời 6.0 của preset.
   *
   * KHÔNG dùng `toneMappingExposure` để bù: exposure là của cả khung hình, hạ nó xuống
   * thì mọi thứ khác (vòm trời, vật tự phát sáng) tối theo.
   *
   * @param {THREE.Object3D} root gốc cây model
   * @param {Object} [opts]
   * @param {'blender'|'gltf'} [opts.unit] 'blender' (mặc định) = quy đổi như trên.
   *   'gltf' = giữ nguyên số trong file, chỉ nhân `scale`.
   * @param {number} [opts.scale] hệ số nhân thêm sau khi quy đổi (mặc định 1)
   * @returns {{count: number, before: number[], after: number[]}}
   */
  normalizeModelLights(root, opts = {}) {
    const unit = opts.unit ?? 'blender';
    const scale = opts.scale ?? 1;
    /** Hệ số W → lumen mà glTF dùng cho đơn vị quang học. */
    const WATTS_TO_LUMENS = 683;
    /** @type {number[]} */
    const before = [];
    /** @type {number[]} */
    const after = [];
    let count = 0;
    if (!root) return { count, before, after };

    root.traverse((/** @type {any} */ o) => {
      if (!o.isLight || o.isAmbientLight || o.isHemisphereLight) return;
      before.push(Number(o.intensity.toFixed(2)));
      if (unit === 'blender') {
        o.intensity = o.isDirectionalLight
          ? o.intensity / WATTS_TO_LUMENS
          : (o.intensity * 4 * Math.PI) / WATTS_TO_LUMENS;
      }
      o.intensity *= scale;
      after.push(Number(o.intensity.toFixed(2)));
      count++;
    });
    return { count, before, after };
  }

  /**
   * Cho đèn CỦA MODEL đổ bóng. GLTFLoader dựng đèn từ `KHR_lights_punctual` với
   * `castShadow = false` — đúng chuẩn, vì glTF không có khái niệm "đèn này đổ bóng".
   * Muốn có bóng thì phải tự bật, và tự canh khung shadow camera y như fitShadows() làm
   * cho mặt trời của preset.
   * @param {THREE.Object3D} root gốc cây model
   * @param {Object} [opts]
   * @param {'directional'|'all'} [opts.mode] 'directional' = chỉ directional + spot
   *   (mặc định). PointLight bị bỏ qua vì bóng của nó là shadow map LẬP PHƯƠNG — sáu mặt,
   *   tức đắt gấp sáu; một model có dăm bảy đèn điểm bật hết là tụt hẳn khung hình.
   * @param {number} [opts.max] tối đa bấy nhiêu đèn được đổ bóng (mặc định 2). Đèn vượt
   *   quá vẫn chiếu sáng, chỉ không đổ bóng.
   * @param {THREE.Object3D} [opts.target] vật cần nằm gọn trong khung bóng (mặc định root)
   * @returns {number} số đèn đã bật đổ bóng
   */
  markModelLightsCastShadow(root, opts = {}) {
    if (!this._shadowsOn || !root) return 0;
    const mode = opts.mode ?? 'directional';
    const max = opts.max ?? 2;
    const target = opts.target ?? root;
    const sphere = new THREE.Box3().setFromObject(target).getBoundingSphere(new THREE.Sphere());
    let n = 0;
    root.traverse((/** @type {any} */ o) => {
      if (!o.isLight || o.isAmbientLight || o.isHemisphereLight) return;
      if (mode !== 'all' && o.isPointLight) return;
      if (n >= max) return;
      o.castShadow = true;
      o.shadow.mapSize.set(this.config.shadowMapSize, this.config.shadowMapSize);
      const cam = o.shadow.camera;
      if (cam.isOrthographicCamera) {
        cam.left = -sphere.radius;
        cam.right = sphere.radius;
        cam.top = sphere.radius;
        cam.bottom = -sphere.radius;
      }
      cam.near = Math.max(0.01, sphere.radius * 0.01);
      cam.far = sphere.radius * 6;
      cam.updateProjectionMatrix();
      const texel = (sphere.radius * 2) / o.shadow.mapSize.x;
      o.shadow.normalBias = texel * VIEW_PRESET.shadow.normalBiasTexels;
      o.shadow.radius = VIEW_PRESET.shadow.softness;
      n++;
    });
    if (n) this.invalidateShadows();
    return n;
  }

  /**
   * Hướng TỚI mặt trời. Bám theo trục camera→target khi `shadow.follow.enabled`, còn
   * không thì trả về hướng cố định của preset.
   * @returns {THREE.Vector3} vector đơn vị
   */
  _sunDirection() {
    const sh = VIEW_PRESET.shadow;
    const f = sh.follow;
    if (!f?.enabled) return new THREE.Vector3(...sh.direction).normalize();

    // Vector TỪ điểm nhìn RA camera — chính là trục camera→target, đảo chiều.
    const v = new THREE.Vector3().subVectors(this.camera.position, this.controls.target);
    if (v.lengthSq() < 1e-9) return new THREE.Vector3(...sh.direction).normalize();
    const sph = new THREE.Spherical().setFromVector3(v);
    const theta = sph.theta + THREE.MathUtils.degToRad(f.azimuthOffsetDeg);
    // phi đo TỪ TRỤC +Y xuống, nên độ cao 32° ứng với phi = 58°.
    const phi = f.elevationDeg == null ? sph.phi : THREE.MathUtils.degToRad(90 - f.elevationDeg);
    return new THREE.Vector3().setFromSpherical(new THREE.Spherical(1, phi, theta)).normalize();
  }

  /**
   * Đặt mặt trời theo một hướng, giữ nguyên vùng đổ bóng đã canh.
   * @param {THREE.Vector3} dir
   */
  _placeSun(dir) {
    if (!this._sun || !this._sunTarget || !this._shadowFit) return;
    const { center, dist } = this._shadowFit;
    this._sunTarget.position.copy(center);
    this._sun.position.copy(center).addScaledVector(dir, dist);
    this._sunDir = dir.clone();
  }

  /**
   * Camera xoay → xoay mặt trời theo. Gọi mỗi lần vẽ, nhưng CHỈ đặt lại (và vẽ lại
   * shadow map) khi hướng đã lệch quá `follow.updateDeg`. Không có ngưỡng này thì mỗi
   * frame xoay là một lần dựng lại shadow map cho toàn bộ model.
   */
  _syncSunToCamera() {
    const f = VIEW_PRESET.shadow.follow;
    if (!this._sun || !f?.enabled || !this._shadowFit) return;
    // Mặt trời đang ẩn (model tự mang đèn) → không có gì để xoay, và nhất là KHÔNG được
    // gọi invalidateShadows: mỗi 3° xoay sẽ dựng lại shadow map cho một cái đèn tắt.
    if (!this._sun.visible) return;
    const dir = this._sunDirection();
    if (this._sunDir && this._sunDir.angleTo(dir) < THREE.MathUtils.degToRad(f.updateDeg)) {
      return;
    }
    this._placeSun(dir);
    this.invalidateShadows();
  }

  /** Yêu cầu vẽ lại shadow map ở frame kế tiếp (autoUpdate đang tắt). */
  invalidateShadows() {
    if (this._shadowsOn) this.renderer.shadowMap.needsUpdate = true;
  }

  /**
   * Đổi cỡ shadow map lúc chạy. boot.js gọi để hạ xuống 1024 trên máy tier "low".
   * @param {number} size
   */
  setShadowMapSize(size) {
    if (!this._sun) return;
    this._sun.shadow.mapSize.set(size, size);
    this._sun.shadow.map?.dispose();
    this._sun.shadow.map = null;
    this.invalidateShadows();
  }

  /** @param {string} hex */
  setBackground(hex) {
    const c = new THREE.Color(hex);
    if (this.scene.background instanceof THREE.Color) this.scene.background.set(c);
    else this.scene.background = c;
    this.renderer.setClearColor(c);
  }

  /** Snapshot renderer.info — debug panel và smoke test đọc. */
  stats() {
    const i = this.renderer.info;
    return {
      calls: i.render.calls,
      triangles: i.render.triangles,
      points: i.render.points,
      lines: i.render.lines,
      geometries: i.memory.geometries,
      textures: i.memory.textures,
      programs: i.programs?.length ?? 0,
      frameCount: this.frameCount,
    };
  }

  // =========================================================================
  // Dispose
  // =========================================================================

  /**
   * Dọn TOÀN BỘ. Bản gốc trong three-lessons chỉ dispose controls + renderer,
   * bỏ sót scene content và không forceContextLoss → leak khi đổi tiết nhiều lần.
   */
  dispose() {
    this.stop();

    document.removeEventListener('visibilitychange', this._onVisibility);
    this._ro.disconnect();

    if (this._onWheel) {
      this.renderer.domElement.removeEventListener('wheel', this._onWheel);
      this._onWheel = null;
    }

    this._updates.length = 0;
    this._renderFn = null;

    // dispose toàn bộ nội dung scene
    for (const child of [...this.scene.children]) {
      disposeObject3D(child);
      this.scene.remove(child);
    }
    this.envMap?.dispose();
    this.scene.environment = null;
    this.scene.background = null;

    this.controls.dispose();
    this.renderer.dispose();
    // giải phóng context ngay, không chờ GC — quan trọng khi có trần 16 context
    this.renderer.forceContextLoss();
  }

  _resize() {
    const parent = this.canvas.parentElement;
    const w = parent?.clientWidth || window.innerWidth;
    const h = parent?.clientHeight || window.innerHeight;
    if (w === 0 || h === 0) return;

    const prevAspect = this.camera.aspect;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    // Canh LẠI khung khi aspect đổi. Bắt buộc vì frameObject() phụ thuộc aspect nhưng
    // thường được gọi trong lesson.init(), có thể TRƯỚC lần _resize() đầu tiên — lúc
    // đó aspect còn là 1 và khung ra sai (đã gặp: quả cầu bị lùi 5.06 thay vì 3.58).
    // KHÔNG canh lại nếu người dùng đã tự zoom — không được giật mất góc nhìn của họ.
    if (this._framed && !this._userZoomed && Math.abs(prevAspect - this.camera.aspect) > 1e-4) {
      this._applyFit(this._framed.object, this._framed.padding);
    }

    // Vẽ lại NGAY, không chờ lượt rAF kế tiếp của _tick(). renderer.setSize() ở trên
    // vừa đổi canvas.width/height — với MỌI canvas (2D hay WebGL), đổi width/height
    // luôn xoá/reset buffer vẽ ngay lập tức, để lại một khung hình trống cho tới khi
    // có gì vẽ lại. Bình thường độ trễ đó chỉ 1 frame (~16ms), không ai thấy — nhưng
    // ResizeObserver không gộp các lần fire liên tiếp, nên khi cạnh panel bên cạnh
    // đang chạy CSS transition (kéo dài hàng trăm ms) hoặc bị kéo tay, _resize() có
    // thể chạy hàng chục lần dồn trong vài frame, để hở nhiều khung trống liên tiếp —
    // thấy được như một nhịp "nháy trắng" (canvas trống lộ ra nền --ol-bg gần trắng
    // phía sau). Gọi _draw() ngay ở đây lấp kín từng khung trống đó, không phải sửa
    // ở phía gọi resize (mọi nơi làm .ol-stage đổi kích thước đều được lợi, không chỉ
    // Whiteboard.js).
    this._draw();

    this.onResize?.(w, h);
  }
}
